import React, { useEffect, useState } from "react";
import SeopageSearch from "../containers/Index/Search/SeopageSearch";
import meetGreetImage from "../common/images/G-1.webp";
import meetGreetImage2 from "../common/images/G-2.webp";

import axios from "axios";
import apiUrl from "../common/config/apiUrl";
import apiKey from "../common/config/apiKey";
import Seo from "../common/Components/Seo/Seo";
import MeetAndGreetSchema from "../common/Components/StructuredData/MeetAndGreetSchema";

// React Icons
import {
  FaCar,
  FaHandshake,
  FaShieldAlt,
  FaSave,
  FaPhoneAlt,
  FaClock,
  FaCheckCircle,
  FaCalendarAlt,
  FaStar,
  FaUserTie,
} from "react-icons/fa";

const MeetAndGreetParking = () => {
  const [airports, setAirports] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const response = await axios.get(`${apiUrl}/airports?key=${apiKey}`);
        if (response?.data?.response_code == 200) {
          setAirports(response?.data?.data);
        }
      } catch (error) {
        console.log("error", error);
      }
    })();
  }, []);

  // SEO data for Meet & Greet
  const meetGreetSEOData = {
    title:
      "Meet & Greet Airport Parking | How It Works & Best Time to Book | Airport Parking Deals",
    description:
      "Discover stress-free Meet & Greet airport parking. Learn how it works in 3 simple steps, when to book for the best price, and compare trusted providers today.",
    keywords:
      "meet and greet airport parking, valet parking airport, airport parking deals, terminal parking, secure airport parking, book airport parking, VIP airport parking",
    url: `${
      process.env.REACT_APP_SITE_URL || "https://airportparkingdeals.co.uk"
    }/meet-and-greet-parking`,
  };


  return (
    <div>
      <Seo
        title={meetGreetSEOData.title}
        description={meetGreetSEOData.description}
        keywords={meetGreetSEOData.keywords}
        url={meetGreetSEOData.url}
        type="website"
      />
      <MeetAndGreetSchema />
      <SeopageSearch
        airports={airports}
        airportName=""
        heroTitle="<span class='text-secondary'>Meet & Greet Parking:</span> The Ultimate in Airport Convenience"
        description1="Tired of the pre-flight stress of finding a parking space, waiting for a bus, and hauling your luggage across a vast car park? Meet & Greet parking is your shortcut to a serene start for any trip."
        description2="It's the VIP treatment your journey deserves."
        features={[
          {
            title: "Terminal Drop-Off",
            description: "Drive directly to your terminal - no parking stress",
            icon: <FaCar />,
          },
          {
            title: "Professional Chauffeur",
            description:
              "Insured, uniformed driver meets you at the terminal entrance",
            icon: <FaUserTie />,
          },
          {
            title: "Secure Storage",
            description:
              "Your car is stored in a secure, off-airport compound",
            icon: <FaShieldAlt />,
          },
          {
            title: "Swift Return",
            description:
              "Your car delivered back to the terminal on your return",
            icon: <FaClock />,
          },
          {
            title: "Premium Service",
            description: "VIP treatment from start to finish",
            icon: <FaStar />,
          },
        ]}
      />

      {/* Hero Section */}
      <section className="md:py-10 py-7 bg-gray-50">
        <div className="px-5 mx-auto max-w-[1280px] sm:px-6 lg:px-8">
          <div className="grid items-center gap-5 lg:grid-cols-2">
            <div>
              <div className="flex flex-wrap items-center justify-center gap-2 mb-2">
                <h1 className="text-3xl font-bold md:text-5xl">
                  Meet & Greet Parking:
                  <span className="px-1 font-bold text-teal-500">
                    The Ultimate in Airport Convenience
                  </span>
                </h1>
              </div>

              <div className="space-y-4 text-gray-700">
                <p>
                  Tired of the pre-flight stress of finding a parking space,
                  waiting for a bus, and hauling your luggage across a vast car
                  park? <strong>Meet & Greet parking</strong> is your shortcut
                  to a serene start for any trip. It's the VIP treatment your
                  journey deserves.
                </p>
                <p>
                  Imagine this: You drive directly to the terminal, a
                  professional chauffeur meets you at the drop-off zone, and you
                  walk straight to check-in.{" "}
                  <strong>Your holiday begins the moment you arrive.</strong>
                </p>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="relative">
                <div className="h-full">
                  <img
                    src={meetGreetImage}
                    alt="Meet & Greet Airport Parking - VIP Service"
                    className="object-cover w-full h-full rounded-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-12 bg-white md:py-16">
        <div className="px-4 mx-auto max-w-[1280px] sm:px-6 md:px-10">
          <div className="mb-12 text-center md:mb-16">
            <h2 className="mb-4 text-2xl font-bold leading-tight text-center sm:text-3xl md:text-4xl text-primary">
              <span className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
                <FaHandshake className="text-secondary" />
                <span>
                  How Meet & Greet Parking Works{" "}
                  <span className="text-teal-500">in 3 Simple Steps</span>
                </span>
              </span>
            </h2>
            <p className="max-w-2xl mx-auto text-sm leading-relaxed text-gray-600 sm:text-base">
              We've made the process effortless, so you can focus on the
              excitement of your trip.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {/* Step 1 */}
            <div className="relative p-6 transition-all duration-300 transform shadow-lg bg-gradient-to-br from-blue-50 to-teal-50 group sm:p-8 rounded-2xl hover:shadow-xl hover:-translate-y-1">
              <div className="absolute top-4 right-4">
                <span className="flex items-center justify-center w-12 h-12 text-xl font-bold text-white rounded-full bg-primary">
                  1
                </span>
              </div>
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-12 h-12 mr-4 transition-colors duration-300 rounded-full bg-primary group-hover:bg-secondary">
                  <FaCar className="text-xl text-white" />
                </div>
                <h3 className="text-xl font-bold transition-colors duration-300 sm:text-2xl text-primary group-hover:text-secondary">
                  Drive & Call
                </h3>
              </div>
              <p className="leading-relaxed text-gray-700">
                On your travel day, drive directly to the airport's terminal
                departures. Once you're a few minutes away, call the dedicated
                number provided in your booking confirmation. This alerts the
                friendly, insured driver that you're arriving.
              </p>
            </div>

            {/* Step 2 */}
            <div className="relative p-6 transition-all duration-300 transform shadow-lg bg-gradient-to-br from-blue-50 to-teal-50 group sm:p-8 rounded-2xl hover:shadow-xl hover:-translate-y-1">
              <div className="absolute top-4 right-4">
                <span className="flex items-center justify-center w-12 h-12 text-xl font-bold text-white rounded-full bg-primary">
                  2
                </span>
              </div>
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-12 h-12 mr-4 transition-colors duration-300 rounded-full bg-primary group-hover:bg-secondary">
                  <FaUserTie className="text-xl text-white" />
                </div>
                <h3 className="text-xl font-bold transition-colors duration-300 sm:text-2xl text-primary group-hover:text-secondary">
                  Meet & Handover
                </h3>
              </div>
              <p className="leading-relaxed text-gray-700">
                A uniformed chauffeur will meet you right outside the terminal
                entrance. They'll perform a quick, joint inspection of your
                vehicle, hand you a copy of the check-in report, and take your
                keys. You simply grab your bags and walk into the airport. No
                queues, no shuttles, no hassle.
              </p>
            </div>

            {/* Step 3 */}
            <div className="relative p-6 transition-all duration-300 transform shadow-lg bg-gradient-to-br from-blue-50 to-teal-50 group sm:p-8 rounded-2xl hover:shadow-xl hover:-translate-y-1">
              <div className="absolute top-4 right-4">
                <span className="flex items-center justify-center w-12 h-12 text-xl font-bold text-white rounded-full bg-primary">
                  3
                </span>
              </div>
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-12 h-12 mr-4 transition-colors duration-300 rounded-full bg-primary group-hover:bg-secondary">
                  <FaClock className="text-xl text-white" />
                </div>
                <h3 className="text-xl font-bold transition-colors duration-300 sm:text-2xl text-primary group-hover:text-secondary">
                  Relax & Reclaim
                </h3>
              </div>
              <p className="leading-relaxed text-gray-700">
                While you're away, your car is stored in a secure, off-airport
                compound. On your return, once you've collected your luggage,
                simply call the same number. Your car will be waiting for you,
                delivered back to the terminal for a swift getaway home.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* When to Book Section */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-secondary/10 to-white">
        <div className="px-4 mx-auto max-w-[1280px] sm:px-6 md:px-10">
          <div className="mb-12 text-center">
            <h2 className="flex items-center justify-center gap-3 mb-4 text-2xl font-bold sm:text-3xl md:text-4xl text-primary">
              <FaCalendarAlt className="hidden text-secondary md:block" />
              When is the Best Time to Book?
            </h2>
            <p className="mb-8 text-xl font-semibold text-teal-600">
              Think Early for the Best Deal & Guaranteed Spot.
            </p>
          </div>

          <div className="grid gap-6 mb-12 md:grid-cols-2 lg:grid-cols-3">
            {/* Best Price */}
            <div className="p-4 transition-all duration-300 bg-white border-2 border-teal-100 shadow-lg rounded-2xl hover:shadow-xl">
              <div className="flex items-center mb-4">
                <FaSave className="mr-3 text-3xl text-secondary" />
                <h3 className="text-xl font-bold text-primary">
                  For the Best Price
                </h3>
              </div>
              <p className="leading-relaxed text-gray-700">
                Booking several weeks or even months in advance locks in the
                most competitive rates. Prices inevitably rise as the travel
                date approaches.
              </p>
            </div>

            {/* Guaranteed Availability */}
            <div className="p-4 transition-all duration-300 bg-white border-2 border-teal-100 shadow-lg rounded-2xl hover:shadow-xl">
              <div className="flex items-center mb-4">
                <FaCheckCircle className="mr-3 text-3xl text-secondary" />
                <h3 className="text-xl font-bold text-primary">
                  For Guaranteed Availability
                </h3>
              </div>
              <p className="leading-relaxed text-gray-700">
                During peak seasons like school holidays, these services sell
                out fast. An early booking ensures your seamless parking
                experience is confirmed.
              </p>
            </div>

            {/* Peace of Mind */}
            <div className="p-4 transition-all duration-300 bg-white border-2 border-teal-100 shadow-lg rounded-2xl hover:shadow-xl">
              <div className="flex items-center mb-4">
                <FaShieldAlt className="mr-3 text-3xl text-secondary" />
                <h3 className="text-xl font-bold text-primary">
                  For Total Peace of Mind
                </h3>
              </div>
              <p className="leading-relaxed text-gray-700">
                Secure your parking at the same time you book your flights. It's
                one less thing to organise, making your pre-trip planning
                completely stress-free.
              </p>
            </div>
          </div>

          <div className="p-8 text-center bg-white shadow-xl rounded-2xl">
            <p className="mb-6 text-lg text-gray-600">
              <strong className="text-primary">Important:</strong> Meet & Greet
              is a premium service with limited capacity, much like the best
              flight seats.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 bg-gradient-to-r from-primary to-secondary">
        <div className="px-4 mx-auto max-w-[1280px] text-center">
          <h2 className="mb-6 text-3xl font-bold text-white md:text-4xl">
            Ready to Experience the Easiest Airport Parking?
          </h2>
          <p className="max-w-3xl mx-auto mb-8 text-lg leading-relaxed text-white/90">
            Let Airport Parking Deals find your perfect Meet & Greet service. We
            compare trusted, fully-vetted providers at your airport, so you get
            a secure, convenient, and great-value deal every time.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/#search-form"
              className="px-8 py-4 text-lg font-semibold transition-all duration-300 transform bg-white rounded-full text-primary hover:bg-gray-100 hover:shadow-xl hover:-translate-y-1"
            >
              Compare Meet & Greet Prices
            </a>
            <a
              href="/contact-us"
              className="px-8 py-4 text-lg font-semibold text-white transition-all duration-300 transform border-2 border-white rounded-full hover:bg-white hover:text-primary hover:-translate-y-1"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-white md:py-16">
        <div className="px-4 mx-auto max-w-[1280px] sm:px-6 md:px-10">
          <div className="grid items-center gap-8 lg:grid-cols-2">
            <div>
              <h2 className="mb-6 text-3xl font-bold md:text-4xl text-primary">
                Why Choose{" "}
                <span className="text-teal-500">Meet & Greet Parking?</span>
              </h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <FaCheckCircle className="flex-shrink-0 mt-1 mr-3 text-2xl text-secondary" />
                  <div>
                    <h3 className="mb-2 text-xl font-semibold text-primary">
                      Ultimate Convenience
                    </h3>
                    <p className="text-gray-700">
                      No parking stress, no shuttle buses, no long walks. Just
                      drive, hand over keys, and walk to check-in.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FaCheckCircle className="flex-shrink-0 mt-1 mr-3 text-2xl text-secondary" />
                  <div>
                    <h3 className="mb-2 text-xl font-semibold text-primary">
                      Time-Saving
                    </h3>
                    <p className="text-gray-700">
                      Perfect for early morning flights or when you're traveling
                      with children and lots of luggage.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FaCheckCircle className="flex-shrink-0 mt-1 mr-3 text-2xl text-secondary" />
                  <div>
                    <h3 className="mb-2 text-xl font-semibold text-primary">
                      Fully Insured & Professional
                    </h3>
                    <p className="text-gray-700">
                      All drivers are fully insured, trained professionals who
                      treat your car with the utmost care.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FaCheckCircle className="flex-shrink-0 mt-1 mr-3 text-2xl text-secondary" />
                  <div>
                    <h3 className="mb-2 text-xl font-semibold text-primary">
                      Secure Storage
                    </h3>
                    <p className="text-gray-700">
                      Your vehicle is stored in a secure, off-airport compound
                      with CCTV and regular patrols.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <img
                src={meetGreetImage2}
                alt="Professional Meet & Greet Service"
                className="object-cover w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MeetAndGreetParking;

